import { useState } from 'react';
import { addDays, format } from 'date-fns';
import { slotApi } from '../../../services';
import { SlotApiConfig } from '../../CustomBlocks/slotBooking/slotTypes';
import {
  hasUnresolvedPlaceholder,
  normalizeApiSlots,
  resolveUrlTemplate,
} from '../../CustomBlocks/slotBooking/slotUtils';

interface TestState {
  running: boolean;
  ok: boolean;
  title: string;
  message: string;
  samples: string[];
}

const IDLE: TestState = { running: false, ok: false, title: '', message: '', samples: [] };

const fail = (title: string, message: string): TestState => ({ ...IDLE, title, message });

/** Calls the configured slot API once so the author can confirm the URL and path work. */
export const useSlotApiTest = () => {
  const [state, setState] = useState<TestState>(IDLE);

  const run = async (api: SlotApiConfig, testValues: Record<string, string>) => {
    const params = new URLSearchParams(window.location.search);
    Object.entries(testValues).forEach(([key, value]) => value && params.set(key, value));

    if (!api.url.trim()) {
      setState(fail('No URL yet', 'Enter the request URL first.'));
      return;
    }
    if (hasUnresolvedPlaceholder(api.url, params)) {
      setState(fail('Missing test values', 'Fill a test value for every URL parameter.'));
      return;
    }

    setState({ ...IDLE, running: true });
    try {
      const today = new Date();
      const filter = api.sendRange
        ? { startAt: format(today, 'yyyy-MM-dd'), endAt: format(addDays(today, 31), 'yyyy-MM-dd') }
        : undefined;
      const data = await slotApi.fetchSlots(resolveUrlTemplate(api.url.trim(), params), filter);
      const slots = normalizeApiSlots(data, api.path);
      setState(
        slots.length
          ? {
              running: false,
              ok: true,
              title: `Connected · ${slots.length} slot${slots.length === 1 ? '' : 's'} found`,
              message: '',
              samples: slots.slice(0, 3).map((slot) => format(new Date(slot.iso), 'EEE d MMM, h:mm a')),
            }
          : fail('No slots found', `The API responded, but nothing usable was at “${api.path || 'root'}”.`),
      );
    } catch (err: any) {
      setState(fail('Request failed', err?.message || 'Unknown error'));
    }
  };

  return { ...state, run, reset: () => setState(IDLE) };
};
